import { useEffect, useState, type ReactNode } from "react";
import { Loader2, WifiOff, RefreshCw, AlertTriangle } from "lucide-react";
import { bootstrapOnce, getBootState, retryBootstrap, subscribeBoot, logPhase, type BootState } from "@/lib/boot";

function removeStaticSplash() {
  if (typeof document === "undefined") return;
  try { (window as any).__BC_BOOT_CLEAR?.(); } catch { /* noop */ }
  const el = document.getElementById("bc-boot-splash");
  if (el) el.remove();
  document.documentElement.classList.remove("bc-booting");
}

function StartupScreen({ slow, offline, error, onRetry }: { slow: boolean; offline: boolean; error: string | null; onRetry: () => void }) {
  return (
    <div className="fixed inset-0 z-[9998] flex flex-col items-center justify-center gap-4 bg-[#4c1d95] px-8 text-center text-white">
      <div className="grid h-20 w-20 place-items-center rounded-2xl bg-white/10 text-2xl font-black tracking-tight">
        BC
      </div>
      <div className="text-lg font-black">Brothers Club</div>

      {offline ? (
        <>
          <WifiOff className="h-6 w-6 opacity-90" />
          <p className="max-w-xs text-sm text-white/80">
            No internet connection. Connect to a network and try again — the app will retry automatically once you are back online.
          </p>
        </>
      ) : error ? (
        <>
          <AlertTriangle className="h-6 w-6 opacity-90" />
          <p className="max-w-xs text-sm text-white/80">We couldn't finish starting the app.</p>
        </>
      ) : (
        <>
          <Loader2 className="h-6 w-6 animate-spin opacity-90" />
          <p className="text-sm text-white/80">
            {slow ? "Taking longer than expected…" : "Loading Brothers Club…"}
          </p>
        </>
      )}

      {(offline || error || slow) && (
        <button
          onClick={onRetry}
          className="mt-2 inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-bold text-[#4c1d95]"
        >
          <RefreshCw className="h-4 w-4" />
          Retry
        </button>
      )}
    </div>
  );
}

/**
 * Gates the app on ONE boot state instead of a pile of unrelated loading
 * flags. It always renders something visible, and the boot state machine is
 * guaranteed to leave "starting" (success, timeout, or offline).
 */
export function AppBootGate({ children }: { children: ReactNode }) {
  const [boot, setBoot] = useState<BootState>(() => getBootState());

  useEffect(() => {
    logPhase("REACT_ROOT_MOUNTED");
    const unsub = subscribeBoot(setBoot);
    setBoot(getBootState());
    void bootstrapOnce();
    return unsub;
  }, []);

  useEffect(() => {
    // The static pre-hydration splash is only removed once React owns the
    // screen, so there is never a white/black frame between the two.
    if (boot.status !== "starting") removeStaticSplash();
  }, [boot.status]);

  if (boot.status === "starting" || boot.status === "offline") {
    return (
      <StartupScreen
        slow={boot.slow}
        offline={boot.status === "offline"}
        error={boot.status === "offline" ? null : boot.error}
        onRetry={() => void retryBootstrap()}
      />
    );
  }

  return <>{children}</>;
}
